import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Box, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Draggable from 'react-draggable';
import MicOnIcon from '~/assets/icons/mic-02.svg';
import MicOffIcon from '~/assets/icons/mic-off-02.svg';
import HangupIcon from '~/assets/icons/container.svg';
import VolOnIcon from '~/assets/icons/volume-high.svg';
import VolOffIcon from '~/assets/icons/volume-mute-02.svg';
import callBgImage from '~/assets/images/call-bg.png';
import type { CallData, CallStatus } from '~/context/CallContext';
import { recoverRemotePlayback } from '~/call/recoverRemotePlayback';
import { applyBrowserDefaultAudioOutput } from '~/hooks/janus/webDevices';

const iconWhiteFilter = 'brightness(0) invert(1)';
const iconBlackFilter = 'brightness(0)';
export interface ActiveCallModalProps {
  callStatus: CallStatus;
  callDuration: number;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  data?: CallData | null;
  callerUsername?: string | null;
  micUnavailable?: boolean;
  speakerUnavailable?: boolean;
  onRemotePlaybackError?: () => void;
  onSpeakerRecovered?: () => void;
  registerRemotePlaybackRecover?: (fn: (() => Promise<boolean>) | null) => void;
  onHangup: () => void;
  onToggleMute?: (muted: boolean) => void;
}

const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const DEFAULT_WIDTH = 280;
const DEFAULT_HEIGHT = 260;
const MIN_WIDTH = 260;
const BASE_MIN_HEIGHT = 200;
const MAX_HEIGHT = 600;
const OFFSET_TOP = 72;
const OFFSET_RIGHT = 24;

const ALERT_BANNER_HEIGHT_PX = 52;
const STATUS_GAP_MAX_PX = 50;
const STATUS_GAP_MIN_PX = 6;

const ActiveCallModal: React.FC<ActiveCallModalProps> = ({
  callStatus,
  callDuration,
  localStream,
  remoteStream,
  data,
 
  micUnavailable = false,
  speakerUnavailable = false,
  onRemotePlaybackError,
  onSpeakerRecovered,
  registerRemotePlaybackRecover,
  onHangup,
  onToggleMute,
}) => {
  const { t } = useTranslation('admin/call');
  const statusLabel = useMemo<Partial<Record<CallStatus, string>>>(
    () => ({
      initiating: t('status.initiating'),
      pending: t('status.pending'),
      accepted: t('status.accepted'),
      calling: t('status.calling'),
    }),
    [t]
  );
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerOff, setIsSpeakerOff] = useState(false);

  const alertBannerCount = (micUnavailable ? 1 : 0) + (speakerUnavailable ? 1 : 0);
  const minModalHeight = Math.max(BASE_MIN_HEIGHT, DEFAULT_HEIGHT) + alertBannerCount * ALERT_BANNER_HEIGHT_PX;

  const [dimensions, setDimensions] = useState({
    width: DEFAULT_WIDTH,
    height: Math.max(DEFAULT_HEIGHT, BASE_MIN_HEIGHT),
  });
  const [position, setPosition] = useState(() => ({
    x:
      typeof window !== 'undefined'
        ? Math.max(0, window.innerWidth - DEFAULT_WIDTH - OFFSET_RIGHT)
        : 0,
    y: typeof window !== 'undefined' ? OFFSET_TOP : 0,
  }));

  const localAudioRef = useRef<HTMLAudioElement>(null);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  const nodeRef = useRef<HTMLDivElement>(null);
  const resizeRef = useRef<HTMLDivElement>(null);

  const avatarPx = useMemo(() => Math.min(64, Math.max(40, Math.floor(dimensions.height * 0.18))), [dimensions.height]);

  useEffect(() => {
    setDimensions((prev) => ({
      ...prev,
      height: Math.max(prev.height, minModalHeight),
    }));
  }, [minModalHeight]);

  useEffect(() => {
    if (!micUnavailable) {
      setIsMuted(false);
    }
  }, [micUnavailable]);

  useEffect(() => {
    if (!registerRemotePlaybackRecover) return undefined;
    registerRemotePlaybackRecover(async () => {
      const ok = await recoverRemotePlayback(remoteAudioRef.current, remoteStream, isSpeakerOff);
      if (ok) onSpeakerRecovered?.();
      return ok;
    });
    return () => registerRemotePlaybackRecover(null);
  }, [registerRemotePlaybackRecover, remoteStream, isSpeakerOff, onSpeakerRecovered]);

  useEffect(() => {
    if (localAudioRef.current && localStream) {
      localAudioRef.current.srcObject = localStream;
      localAudioRef.current.muted = true;
    }
  }, [localStream]);

  useEffect(() => {
    const el = remoteAudioRef.current;
    if (!el) return;
    if (!remoteStream) {
      el.srcObject = null;
      return;
    }

    let cancelled = false;
    const tracks = remoteStream.getAudioTracks();

    const tryPlay = () => {
      if (cancelled) return;
      void el.play().catch(() => {
        onRemotePlaybackError?.();
      });
    };

    void (async () => {
      await applyBrowserDefaultAudioOutput(el);
      if (cancelled) return;
      el.srcObject = remoteStream;
      el.muted = isSpeakerOff;
      el.volume = 1;
      tryPlay();
    })();

    const unmuteHandlers: Array<() => void> = [];
    tracks.forEach((track) => {
      if (!track.muted) return;
      const onUnmute = () => tryPlay();
      track.addEventListener('unmute', onUnmute);
      unmuteHandlers.push(() => track.removeEventListener('unmute', onUnmute));
    });

    return () => {
      cancelled = true;
      unmuteHandlers.forEach((off) => off());
    };
  }, [remoteStream, isSpeakerOff, onRemotePlaybackError]);

  useEffect(() => {
    const el = remoteAudioRef.current;
    if (!el || !onRemotePlaybackError) return;
    const onErr = () => onRemotePlaybackError();
    el.addEventListener('error', onErr);
    return () => el.removeEventListener('error', onErr);
  }, [onRemotePlaybackError, remoteStream]);

  const toggleMute = useCallback(() => {
    if (micUnavailable) return;
    setIsMuted((prev) => {
      const next = !prev;
      onToggleMute?.(next);
      return next;
    });
  }, [micUnavailable, onToggleMute]);

  const toggleSpeaker = useCallback(() => {
    if (speakerUnavailable) return;
    if (remoteAudioRef.current) {
      remoteAudioRef.current.muted = !remoteAudioRef.current.muted;
      setIsSpeakerOff((prev) => !prev);
    }
  }, [speakerUnavailable]);

  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = dimensions.width;
    const startH = dimensions.height;

    const onMove = (moveEv: MouseEvent) => {
      setDimensions({
        width: Math.max(MIN_WIDTH, Math.min(600, startW + (moveEv.clientX - startX))),
        height: Math.max(minModalHeight, Math.min(MAX_HEIGHT, startH + (moveEv.clientY - startY))),
      });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const durationText =
    callStatus === 'oncall'
      ? formatDuration(callDuration)
      : statusLabel[callStatus] || t('status.connectingFallback');

  const showMicAsOff = micUnavailable || isMuted;
  const showVolAsOff = speakerUnavailable || isSpeakerOff;

  return (
    <Draggable
      nodeRef={nodeRef}
      cancel='.active-call-interactive'
      position={position}
      onDrag={(_, d) => setPosition({ x: d.x, y: d.y })}
    >
      <Paper
        ref={nodeRef}
        elevation={10}
        sx={(theme) => ({
          position: 'fixed',
          top: 0,
          left: 0,
          width: dimensions.width,
          height: dimensions.height,
          zIndex: theme.zIndex.modal + 2000,
          borderRadius: '24px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          color: 'white',
          pt: '32px',
          pb: '28px',
          cursor: 'grab',
          backgroundImage: `linear-gradient(180deg, rgba(15, 48, 87, 0.42) 0%, rgba(0, 48, 44, 0.38) 100%), url(${callBgImage})`,
          backgroundSize: '100% 100%',
          backgroundRepeat: 'no-repeat',
        })}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            px: 1,
            pb: 1,
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
            }}
          >
            <Avatar
              src={data?.icon}
              sx={{ width: avatarPx, height: avatarPx, border: '2px solid rgba(255,255,255,0.22)' }}
            />
            <Typography variant='body2' sx={{ fontWeight: 600, fontSize: '16px', textAlign: 'center' }}>
              {data?.imei || data?.username || t('unknownTarget')}
            </Typography>
            <Typography
              variant='caption'
              sx={{ opacity: 0.9, fontSize: '13px', textAlign: 'center', lineHeight: 1.35 }}
            >
              {data?.display_name}
            </Typography>
          </Box>

          <Box
            sx={{
              flex: alertBannerCount > 0 ? '0 0 auto' : '1 1 auto',
              minHeight: alertBannerCount > 0 ? 0 : STATUS_GAP_MIN_PX,
              maxHeight: alertBannerCount > 0 ? undefined : STATUS_GAP_MAX_PX,
              width: '100%',
            }}
          />

          <Box sx={{ flexShrink: 0, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography
              variant='caption'
              sx={{ fontSize: '14px', fontWeight: 500, textAlign: 'center' }}
            >
              {durationText}
            </Typography>
          {micUnavailable ? (
            <Typography
              variant='caption'
              component='p'
              sx={{
                mt: '8px',
                flexShrink: 0,
                fontSize: '12px',
                fontWeight: 600,
                textAlign: 'center',
                px: 2,
                py: '6px',
                borderRadius: '8px',
                bgcolor: '#FEECEC',
                color: '#D92D20',
              }}
            >
              {t('modal.micUnavailableHighlight')}
            </Typography>
          ) : null}
          {speakerUnavailable ? (
            <Typography
              variant='caption'
              component='p'
              sx={{
                mt: micUnavailable ? '6px' : '8px',
                flexShrink: 0,
                fontSize: '12px',
                fontWeight: 600,
                textAlign: 'center',
                px: 2,
                py: '6px',
                borderRadius: '8px',
                bgcolor: '#FEECEC',
                color: '#D92D20',
              }}
            >
              {t('modal.speakerUnavailableHighlight')}
            </Typography>
          ) : null}
          </Box>
        </Box>

        <audio ref={remoteAudioRef} autoPlay playsInline style={{ display: 'none' }} />
        <audio ref={localAudioRef} autoPlay playsInline muted style={{ display: 'none' }} />

        <Box className='active-call-interactive' sx={{ display: 'flex', gap: '16px', mt: 'auto', flexShrink: 0 }}>
          <Box
            onClick={micUnavailable ? undefined : toggleMute}
            role='button'
            aria-disabled={micUnavailable}
            sx={{
              width: 46,
              height: 46,
              borderRadius: '50%',
              background: showMicAsOff ? '#FFF' : 'rgba(255, 255, 255, 0.15)',
              boxShadow: micUnavailable ? '0 0 0 2px #D92D20' : 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: micUnavailable ? 'not-allowed' : 'pointer',
              pointerEvents: micUnavailable ? 'none' : 'auto',
              opacity: micUnavailable ? 0.92 : 1,
            }}
          >
            <img
              src={showMicAsOff ? MicOffIcon : MicOnIcon}
              alt='Mic'
              width={24}
              height={24}
              style={{
                filter: showMicAsOff ? iconBlackFilter : iconWhiteFilter,
              }}
            />
          </Box>
          <Box
            onClick={onHangup}
            sx={{
              width: 46,
              height: 46,
              borderRadius: '50%',
              background: '#D92D20',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <img src={HangupIcon} alt='Hang up' width={26} height={26} style={{ filter: iconWhiteFilter }} />
          </Box>
          <Box
            onClick={speakerUnavailable ? undefined : toggleSpeaker}
            role='button'
            aria-disabled={speakerUnavailable}
            sx={{
              width: 46,
              height: 46,
              borderRadius: '50%',
              background: showVolAsOff ? '#FFF' : 'rgba(255, 255, 255, 0.15)',
              boxShadow: speakerUnavailable ? '0 0 0 2px #D92D20' : 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: speakerUnavailable ? 'not-allowed' : 'pointer',
              pointerEvents: speakerUnavailable ? 'none' : 'auto',
              opacity: speakerUnavailable ? 0.92 : 1,
            }}
          >
            <img
              src={showVolAsOff ? VolOffIcon : VolOnIcon}
              alt='Speaker'
              width={24}
              height={24}
              style={{
                filter: showVolAsOff ? iconBlackFilter : iconWhiteFilter,
              }}
            />
          </Box>
        </Box>

        <Box
          ref={resizeRef}
          onMouseDown={handleResizeMouseDown}
          className='active-call-interactive'
          sx={{ position: 'absolute', bottom: 4, right: 4, cursor: 'nwse-resize', p: '6px' }}
        >
          <svg width={14} height={14} viewBox='0 0 18 18' fill='none' aria-hidden>
            <line x1='11' y1='15' x2='15' y2='11' stroke='rgba(255,255,255,0.4)' strokeWidth='2' strokeLinecap='round' />
            <line x1='7' y1='15' x2='15' y2='7' stroke='rgba(255,255,255,0.4)' strokeWidth='2' strokeLinecap='round' />
          </svg>
        </Box>
      </Paper>
    </Draggable>
  );
};

export default ActiveCallModal;
